import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  libraryModuleFormSchema,
  LibraryModuleFormData,
} from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleForm/types";
import {
  emptyLibraryModuleFormData,
  formDataToLibraryModulePayload,
} from "@/modules/learningCenter/features/ModuleLibrary/components/LibraryModuleForm/mapper";
import {
  createLibraryModuleAction,
  resetCreateUpdateLibraryModuleState,
} from "@/modules/learningCenter/features/ModuleLibrary/slices/createUpdateLibraryModuleSlice";

export const useCreateLibraryModule = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const isSubmitting = useAppSelector(
    (state: RootState) => state.createUpdateLibraryModule.loadingCreate,
  );

  const form = useForm<LibraryModuleFormData>({
    resolver: zodResolver(libraryModuleFormSchema),
    defaultValues: emptyLibraryModuleFormData(),
    mode: "onChange",
  });

  useEffect(() => {
    return () => {
      dispatch(resetCreateUpdateLibraryModuleState());
    };
  }, [dispatch]);

  const submitForm = form.handleSubmit((data) => {
    dispatch(
      createLibraryModuleAction(
        formDataToLibraryModulePayload(data),
        (module) => {
          showNotification(
            "Module created",
            `"${module.title}" was created as a draft.`,
            "success",
          );
          navigate(`/admin/learning-center/module-library/${module.uuid}/edit`);
        },
      ),
    );
  });

  const handleCancel = () => {
    navigate("/admin/learning-center/module-library");
  };

  return {
    form,
    isSubmitting,
    submitForm,
    handleCancel,
  };
};
